import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

export default function LoginPage() {
  const [mobile,     setMobile]     = useState('');
  const [otp,        setOtp]        = useState('');
  const [step,       setStep]       = useState('mobile');
  const [loading,    setLoading]    = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleMobileSubmit = async () => {
    if (!/^[6-9]\d{9}$/.test(mobile))
      return toast.error('Enter a valid 10-digit mobile number');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/check-user', { mobile });
      if (data.exists) {
        const { data: loginData } = await api.post('/auth/login-direct', { mobile });
        login(loginData.user, { accessToken: loginData.accessToken, refreshToken: loginData.refreshToken });
        toast.success('Welcome back! ');
        navigate(!loginData.isProfileComplete ? '/complete-profile' : '/', { replace: true });
      } else {
        const { data: otpData } = await api.post('/auth/send-otp', { mobile });
        setStep('otp');
        if (otpData?.otp) { setOtp(otpData.otp.toString()); setAutoFilled(true); toast.success('OTP pre-filled for testing'); }
        else toast.success('OTP sent to your mobile');
      }
    } catch (err) { toast.error(err.response?.data?.message || 'Something went wrong.'); }
    finally { setLoading(false); }
  };

  const handleOtpSubmit = async () => {
    if (otp.length !== 6) return toast.error('Enter the 6-digit OTP');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/verify-otp', { mobile, otp });
      login(data.user, { accessToken: data.accessToken, refreshToken: data.refreshToken });
      toast.success(data.isNewUser ? 'OTP verified! Please select your role.' : 'Welcome back! ');
      const dest = data.isNewUser ? '/select-role' : !data.isProfileComplete ? '/complete-profile' : '/';
      navigate(dest, { replace: true });
    } catch (err) { setAutoFilled(false); toast.error(err.response?.data?.message || 'Invalid OTP.'); }
    finally { setLoading(false); }
  };

  const handleResendOtp = async () => {
    try {
      const { data } = await api.post('/auth/send-otp', { mobile });
      if (data?.otp) { setOtp(data.otp.toString()); setAutoFilled(true); toast.success('OTP resent & pre-filled ✓'); }
      else { setOtp(''); setAutoFilled(false); toast.success('OTP resent'); }
    } catch { toast.error('Failed to resend OTP'); }
  };

  const handleWhatsappClick = () => {
    // No WhatsApp-auth endpoint exists in this codebase yet — placeholder only.
    toast('WhatsApp sign-in is coming soon', { icon: '💬' });
  };

  const G  = '#1A6B3C';   // dark green
  const G2 = '#2F9E58';   // lighter green (gradient end)
  const LIGHT = '#EAF3DE';

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(160deg, #F3FBF0 0%, #E9F7E4 45%, #F6FBF4 100%)`,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '24px 20px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      position: 'relative', overflow: 'hidden',
    }}>

      {/* Background soft blobs */}
      <div style={{ position:'absolute', top:'-100px', left:'-100px', width:340, height:340, borderRadius:'50%', background:`radial-gradient(circle, ${LIGHT} 0%, transparent 70%)`, pointerEvents:'none' }} />
      <div style={{ position:'absolute', bottom:'-80px', right:'-80px', width:300, height:300, borderRadius:'50%', background:`radial-gradient(circle, ${LIGHT} 0%, transparent 70%)`, pointerEvents:'none' }} />

      {/* Decorative leaves (emoji, corners) */}
      <div style={{ position:'absolute', top:20, left:12, fontSize:56, opacity:.5, transform:'rotate(-18deg)', pointerEvents:'none' }}>🍃</div>
      <div style={{ position:'absolute', top:90, left:60, fontSize:30, opacity:.4, transform:'rotate(10deg)', pointerEvents:'none' }}></div>
      <div style={{ position:'absolute', bottom:40, right:16, fontSize:60, opacity:.5, transform:'rotate(15deg)', pointerEvents:'none' }}></div>
      <div style={{ position:'absolute', bottom:120, right:70, fontSize:28, opacity:.4, transform:'rotate(-12deg)', pointerEvents:'none' }}>🍃</div>

      {/* ── LOGO ── */}
      <div style={{ textAlign:'center', marginBottom:32, position:'relative', zIndex:1 }}>
        <div style={{
          width: 72, height: 72, borderRadius: 20,
          background: `linear-gradient(135deg, ${G}, ${G2})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 34, margin: '0 auto 14px',
          boxShadow: `0 10px 26px ${G}40`,
        }}></div>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: '#1a1a1a', margin: 0, letterSpacing: -0.5 }}>
          Zeva<span style={{ color: G }}>Zeva</span>
        </h1>
        <p style={{ fontSize: 12.5, color: '#6B7280', margin: '6px 0 0', letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 600 }}>
          Fresh · Direct · Trusted
        </p>
      </div>

      {/* ── CARD ── */}
      <div style={{
        width: '100%', maxWidth: 400, position: 'relative', zIndex: 1,
        background: 'white',
        borderRadius: 24, overflow: 'hidden',
        boxShadow: '0 20px 50px rgba(26,107,60,.14)',
      }}>
        <div style={{ padding: '26px 24px 24px' }}>

          {step === 'mobile' ? (
            <>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom: 22 }}>
                <div style={{ width:44, height:44, borderRadius:'50%', background:LIGHT, color:G, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>👤</div>
                <div>
                  <h2 style={{ fontSize: 19, fontWeight: 800, color: '#1a1a1a', margin: '0 0 2px' }}>Sign In</h2>
                  <p style={{ fontSize: 12.5, color: '#6B7280', margin: 0 }}>Enter your mobile number to continue</p>
                </div>
              </div>

              {/* Mobile input */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ fontSize: 12.5, fontWeight: 700, color: G, display: 'block', marginBottom: 8 }}>Mobile Number</label>
                <div style={{
                  display: 'flex', alignItems: 'center',
                  background: '#FAFBFA', borderRadius: 14,
                  border: `1.5px solid ${mobile.length === 10 ? G : '#E5E7EB'}`,
                  overflow: 'hidden', transition: 'border .2s',
                }}>
                  <div style={{ padding: '14px 0px', color: '#374151', fontSize: 14, fontWeight: 700, borderRight: '1px solid #E5E7EB', flexShrink: 0, letterSpacing: .5 }}>+91</div>
                  <input
                    type="tel" inputMode="numeric" maxLength={10}
                    placeholder="9876543210" value={mobile}
                    onChange={e => setMobile(e.target.value.replace(/\D/g,'').slice(0,10))}
                    onKeyDown={e => e.key === 'Enter' && handleMobileSubmit()}
                    autoFocus
                    style={{
                      flex: 1, border: 'none', outline: 'none', background: 'transparent',
                      color: '#1a1a1a', fontSize: 16, padding: '14px 12px', letterSpacing: 1,
                    }}
                  />
                  {mobile.length === 10 && <span style={{ paddingRight: 14, color: G, fontSize: 18 }}>✓</span>}
                </div>
              </div>

              <button
                onClick={handleMobileSubmit}
                disabled={loading || mobile.length !== 10}
                style={{
                  width: '100%', padding: '14px', border: 'none', borderRadius: 14,
                  background: mobile.length === 10 ? `linear-gradient(135deg, ${G}, ${G2})` : '#EEF1EC',
                  color: mobile.length === 10 ? 'white' : '#9CA3AF',
                  fontSize: 14.5, fontWeight: 700, cursor: mobile.length === 10 ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'all .2s',
                  boxShadow: mobile.length === 10 ? `0 6px 18px ${G}35` : 'none',
                }}
              >
                {loading ? <><span className="spinner spinner--sm" /> Checking...</> : <>Continue <span style={{ fontSize: 16 }}>→</span></>}
              </button>

              {/* OR divider */}
              <div style={{ display:'flex', alignItems:'center', gap:10, margin:'18px 0' }}>
                <div style={{ flex:1, height:1, background:'#E5E7EB' }} />
                <span style={{ fontSize:11.5, color:'#9CA3AF', fontWeight:600 }}>OR</span>
                <div style={{ flex:1, height:1, background:'#E5E7EB' }} />
              </div>

              {/* <button
                onClick={handleWhatsappClick}
                style={{
                  width: '100%', padding: '13px', borderRadius: 14,
                  background: 'white', border: `1.5px solid ${G}`, color: G,
                  fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                💬 Continue with WhatsApp
              </button> */}
            </>
          ) : (
            <>
              <div style={{ marginBottom: 22 }}>
                <button onClick={() => { setStep('mobile'); setOtp(''); setAutoFilled(false); }}
                  style={{ background: 'none', border: 'none', color: G, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, padding: 0, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                  ← Back
                </button>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:44, height:44, borderRadius:'50%', background:LIGHT, color:G, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>🔐</div>
                  <div>
                    <h2 style={{ fontSize: 19, fontWeight: 800, color: '#1a1a1a', margin: '0 0 2px' }}>Verify OTP</h2>
                    <p style={{ fontSize: 12.5, color: '#6B7280', margin: 0 }}>
                      Sent to <span style={{ color: G, fontWeight: 700 }}>+91 {mobile}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* OTP box */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ fontSize: 12.5, fontWeight: 700, color: G, display: 'block', marginBottom: 8 }}>Enter 6-digit OTP</label>
                <input
                  type="tel" inputMode="numeric" maxLength={6}
                  placeholder="• • • • • •" value={otp}
                  onChange={e => { setAutoFilled(false); setOtp(e.target.value.replace(/\D/g,'').slice(0,6)); }}
                  onKeyDown={e => e.key === 'Enter' && handleOtpSubmit()}
                  autoFocus
                  style={{
                    width: '100%', border: `1.5px solid ${otp.length === 6 ? G : '#E5E7EB'}`,
                    borderRadius: 14, padding: '14px', fontSize: 26, fontWeight: 700,
                    letterSpacing: 10, outline: 'none', textAlign: 'center',
                    background: '#FAFBFA', color: '#1a1a1a', boxSizing: 'border-box',
                    transition: 'border .2s',
                  }}
                />
                {autoFilled && (
                  <p style={{ fontSize: 11.5, color: G, marginTop: 6, textAlign: 'center' }}>✓ OTP pre-filled (testing mode)</p>
                )}
              </div>

              <button
                onClick={handleOtpSubmit}
                disabled={loading || otp.length !== 6}
                style={{
                  width: '100%', padding: '14px', border: 'none', borderRadius: 14,
                  background: otp.length === 6 ? `linear-gradient(135deg, ${G}, ${G2})` : '#EEF1EC',
                  color: otp.length === 6 ? 'white' : '#9CA3AF',
                  fontSize: 14.5, fontWeight: 700, cursor: otp.length === 6 ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'all .2s',
                  boxShadow: otp.length === 6 ? `0 6px 18px ${G}35` : 'none',
                }}
              >
                {loading ? <><span className="spinner spinner--sm" /> Verifying...</> : <>Verify OTP <span style={{ fontSize: 16 }}>→</span></>}
              </button>

              <p style={{ textAlign: 'center', marginTop: 16, fontSize: 12.5, color: '#6B7280' }}>
                Didn't receive?{' '}
                <button onClick={handleResendOtp}
                  style={{ color: G, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12.5 }}>
                  Resend OTP
                </button>
              </p>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <p style={{ fontSize: 12, color: '#6B7280', textAlign: 'center', marginTop: 20, lineHeight: 1.6, position: 'relative', zIndex: 1, display:'flex', alignItems:'center', gap:6 }}>
        🛡️ Your data is safe and secure with us
      </p>
    </div>
  );
}